/**
 * Renders a live preview of the customer's personalised label: the chosen
 * design's image with their typed Name / Age-Date / Message lines drawn on
 * top, redrawn on every change to the design choice or the text fields.
 *
 * Each label design (Shopify Admin > Content > Metaobjects > Label Design)
 * can set its own font, font size and text colour for the Name and for the
 * Age/Date + Message lines - no code changes needed to retune a design.
 * Fonts are loaded from Google Fonts by family name at runtime, so typing a
 * new family into a design's "Name font" / "Message font" field just works.
 *
 * Designs belonging to the "Branded Logo" category (offered on every
 * personalised product, plus any product with custom.branded switched on -
 * see snippets/personalisation-fields.liquid) work a little differently:
 * instead of Name/Age text, the customer enters a Company Name and uploads
 * their logo, via a custom-styled file button rather than the browser's own
 * file-input chrome (see the [data-file-input] handling in initBlock()
 * below), and can optionally add a single line of message underneath (same
 * "Message Line 1" cart property as the standard fields, but a distinct DOM
 * input - see brandedMessageInput below - kept disabled while inactive so
 * only one value per property name is ever submitted). The Company Name
 * reuses the same "name" text role/typography as an ordinary design, and
 * the Message reuses the "line1" role - snippets/personalisation-fields.liquid
 * hard-overrides both to Cinzel (Company Name) and Pinyon Script (Message),
 * with capitals never forced, for every Branded Logo colour.
 *
 * While a Branded Logo design is selected, the customer can drag their
 * uploaded logo to reposition it, or drag the handle in its bottom-right
 * corner to resize it - resizing always keeps it centred horizontally on
 * the label (dragging to move it, not resize it, can still place it
 * off-centre on purpose). This is the same interaction the very first
 * version of this feature used (logoTransform / computeContainRect /
 * drawImageWithHandle below): a persistent, pointer-driven rect in the same
 * fixed 1500x1000 "design space" every other drawing calculation uses,
 * seeded to a sensible centred/contained size the moment a logo is
 * uploaded, and carried across redraws from then on.
 *
 * Photo Labels designs (any Label Design with its "Photo upload" metaobject
 * field switched on) offer the exact same drag-to-reposition/drag-to-resize
 * interaction for the customer's uploaded photo, sharing the same underlying
 * machinery (computeContainRect / hitTestRect / activeImageKind /
 * drawImageWithHandle below) - just seeded from that design's own
 * photo_safe_top / photo_safe_bottom / margin_x fields instead of the
 * Branded Logo's fixed constants. The two are mutually exclusive (Photo
 * Labels designs live in the standard category, Branded Logo is its own
 * separate category), so at most one draggable/resizable image is ever
 * active on screen at once - see activeImageKind() in initBlock() below.
 *
 * The uploaded logo has its background automatically stripped on-device
 * (best-effort, no upload to any third party) so it sits cleanly on the
 * coloured backgrounds without the customer needing to prepare a
 * transparent file themselves - see stripNearWhiteBackground() below. The
 * "Remove background automatically" checkbox that controls this is always
 * visible (see snippets/personalisation-fields.liquid - it doubles as a
 * spacer that keeps "Upload your logo" level with "Upload your own
 * ready-made design"), and defaults to on; it simply has no visible effect
 * until a logo is actually uploaded. Switching between the standard
 * category fields and the Branded Logo fields (or back) clears whichever
 * set just became hidden, so no stale text or file carries over into the
 * cart properties.
 *
 * Two canvases share the same drawing code: the visible one the customer
 * drags/resizes on (data-personalisation-canvas), which also shows the
 * dashed outline and resize handle around the logo; and a hidden one kept
 * permanently in sync (data-personalisation-export-canvas), which never
 * draws that handle overlay - it's what actually gets exported for the
 * order, so the drag/resize UI chrome never ends up baked into the printed
 * label.
 *
 * On add-to-cart, the hidden export canvas is exported as a PNG and
 * attached to the hidden `properties[Label Preview]` file input. Shopify's
 * native cart file-upload handling (the same mechanism themes use for
 * "upload your own photo" products) then uploads it automatically and
 * stores a link to it on the order - no app or custom backend required. If
 * image export fails for any reason (unsupported browser, network hiccup
 * loading the design image, etc.) the order still carries the typed-out
 * text properties (and, for Branded Logo, the original uploaded logo file
 * via `properties[Company Logo]`) for staff to work from, so nothing is
 * lost - the visual is a bonus, not a dependency.
 *
 * Shopify renames every uploaded file (Label Preview, and Company Logo) to
 * its own long generated filename on its file storage, shown as the link
 * text on the order - the theme has no control over that. So on every
 * submit a short `properties[Label Reference]` (a random 4-digit number) is
 * also set, purely as a quick, human-friendly number staff can jot down
 * against the label when picking/printing an order - not a guarantee of
 * global uniqueness, just a fast at-a-glance reference alongside the order.
 *
 * "Upload your own ready-made design" (within the Branded Logo/Corporate
 * Branding fields only) lets the customer bypass Company Name/Logo/design
 * selection entirely and hand over a single finished file to be printed
 * exactly as supplied, no compositing. Its box sits permanently alongside
 * "Upload your logo" (per Susie's request, 26 Sep - originally a
 * click-to-reveal toggle that hid the standard fields; now both are visible
 * at once, so the customer sees straight away that either option is
 * available) - whichever one the customer actually fills in is the one
 * that's required and used (see the data-required-path attributes in
 * snippets/personalisation-fields.liquid and validateEitherGroup() in
 * assets/personalisation.js). That file is submitted under the same
 * `properties[_Label Preview]` name the composited canvas export normally
 * uses, via a second file input kept mutually exclusive with it purely in
 * JS (see ownDesignActive() / syncOwnDesignExclusivity() below) - never
 * both at once, same idea as the standard-vs-branded Message field, just
 * driven by whether a file has actually been chosen rather than by a
 * hidden/shown "mode". The design picker and live-preview canvas stay
 * visible and usable throughout - a customer can still browse designs, or
 * change their mind and switch from one upload box to the other, at any
 * point - except the design picker's own "please choose a design"
 * requirement is skipped while a ready-made design is actually uploaded
 * (see data-skip-when-owndesign in the liquid file), since no swatch colour
 * is relevant to a fully custom upload. The submit handler skips canvas
 * export entirely while a ready-made design is active, and lets the
 * browser submit that file natively.
 *
 * The live preview canvas shows this uploaded file directly (contain-fit,
 * centred, no text or logo compositing - see drawOwnDesignPreview() below)
 * the moment it's chosen, in place of the normal design-swatch compositing.
 * Added 26 Sep after Susie reported uploading a ready-made design gave no
 * visual confirmation it had actually been accepted - since this file is
 * printed exactly as supplied, showing it as-is on the canvas is a true
 * preview, not a compositing step. Cleared (falling back to the normal
 * design preview) the moment the file is removed or the customer switches
 * to a different category.
 *
 * Separately - "Upload your logo"'s preview needs a background colour
 * design actually selected, since the logo draws on top of it; with no
 * design selected, drawLabel() below has nothing to draw at all (see its
 * `if (!cfg)` check) and the canvas stays blank, which is what made an
 * uploaded logo look like it "wasn't showing" if the customer hadn't yet
 * clicked one of the four colour swatches. Fixed by defaulting Corporate
 * Branding to its first swatch (White) the moment the category opens - see
 * showCategory() in snippets/personalisation-fields.liquid - so a valid
 * design is always selected and the preview is never blank.
 *
 * This file is only loaded on product pages that render the personalisation
 * fields (see snippets/personalisation-fields.liquid), so it has zero
 * effect on any other page.
 */
(function () {
  'use strict';

  var LOGICAL_WIDTH = 1500;
  var LOGICAL_HEIGHT = 1000;

  var imageCache = {};
  var loadedFontKey = '';

  // Canvas text (especially the branded Cinzel/Pinyon Script fonts) looks
  // noticeably crisper when the canvas's actual pixel buffer is rendered at
  // a higher resolution than its on-screen CSS size and the browser is left
  // to scale it down, rather than drawing at exactly 1x - the standard
  // "retina canvas" technique. Always supersampling by at least 2x (not
  // just matching devicePixelRatio, which is 1 on plenty of ordinary
  // monitors, not just phones) keeps the preview label looking sharp
  // everywhere; capped at 3x so memory and the exported PNG's size stay
  // sensible. All drawing code below still works entirely in the fixed
  // 1500x1000 "logical" space - see the ctx.setTransform() call in
  // drawLabel() that maps it onto this larger physical buffer.
  var RENDER_SCALE = Math.min(Math.max(window.devicePixelRatio || 1, 2), 3);

  function loadImage(src, onReady) {
    if (!src) {
      onReady(null);
      return;
    }
    if (imageCache[src] && imageCache[src].complete) {
      onReady(imageCache[src]);
      return;
    }
    var img = imageCache[src];
    if (!img) {
      img = new Image();
      img.crossOrigin = 'anonymous';
      imageCache[src] = img;
      img.src = src;
    }
    img.addEventListener('load', function () {
      onReady(img);
    });
    img.addEventListener('error', function () {
      onReady(null);
    });
  }

  function loadFileAsImage(file, callback) {
    if (!file) {
      callback(null);
      return;
    }
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.addEventListener('load', function () {
      callback(img);
    });
    img.addEventListener('error', function () {
      callback(null);
      URL.revokeObjectURL(url);
    });
    img.src = url;
  }

  /**
   * Best-effort background removal for a customer-uploaded logo: samples a
   * reference background colour from whichever corners are light enough to
   * plausibly be background, then makes every pixel close to that colour
   * transparent, with a short feather so edges don't look jagged. This is
   * deliberately simple (no server, no third-party API, no ML model) - it
   * works well for the common case of a logo on a plain white/near-white
   * background, which covers most company logos exported from Word, Canva,
   * a website's media kit, etc. It intentionally leaves the image untouched
   * if none of the corners are light, so a logo that already fills the
   * frame edge-to-edge isn't mangled. Because the live preview redraws
   * immediately, the customer can always see whether their particular logo
   * worked well before adding to cart.
   *
   * Matches every pixel against the sampled reference colour directly
   * (rather than flood-filling out from the corners), so background-
   * coloured pixels fully enclosed by the logo's own artwork - inside a
   * circular monogram or badge, the bowl of a letter like O/A/P/D, a ring
   * logo, etc. - are cleared too, not just the outer edges.
   */
  function stripNearWhiteBackground(img) {
    var w = img.naturalWidth || img.width;
    var h = img.naturalHeight || img.height;
    var off = document.createElement('canvas');
    off.width = w;
    off.height = h;
    var octx = off.getContext('2d');
    octx.drawImage(img, 0, 0, w, h);

    var imageData;
    try {
      imageData = octx.getImageData(0, 0, w, h);
    } catch (e) {
      // Cross-origin or read failure - fall back to the untouched image
      // rather than blocking the preview.
      return off;
    }

    var data = imageData.data;

    function idx(x, y) {
      return (y * w + x) * 4;
    }

    // Only corners that are reasonably light count as background - avoids
    // stripping a logo that fills the entire frame edge-to-edge. Average
    // whichever qualify, so a logo with e.g. one busy corner and three
    // plain-white ones still gets a clean reference colour.
    var corners = [
      [0, 0],
      [w - 1, 0],
      [0, h - 1],
      [w - 1, h - 1]
    ];
    var refR = 0, refG = 0, refB = 0, refCount = 0;
    corners.forEach(function (corner) {
      var p = idx(corner[0], corner[1]);
      var r = data[p], g = data[p + 1], b = data[p + 2];
      if (r > 205 && g > 205 && b > 205) {
        refR += r;
        refG += g;
        refB += b;
        refCount++;
      }
    });
    if (!refCount) return off;
    refR /= refCount;
    refG /= refCount;
    refB /= refCount;

    var threshold = 28; // colour-distance tolerance: fully transparent below this
    var featherTo = 60; // colour-distance up to this fades out rather than a hard edge

    for (var p2 = 0; p2 < data.length; p2 += 4) {
      if (data[p2 + 3] === 0) continue;
      var dr = data[p2] - refR;
      var dg = data[p2 + 1] - refG;
      var db = data[p2 + 2] - refB;
      var dist = Math.sqrt(dr * dr + dg * dg + db * db);
      if (dist <= threshold) {
        data[p2 + 3] = 0;
      } else if (dist < featherTo) {
        var fade = (dist - threshold) / (featherTo - threshold);
        data[p2 + 3] = Math.round(data[p2 + 3] * fade);
      }
    }

    octx.putImageData(imageData, 0, 0);
    return off;
  }

  function ensureFontsLoaded(designs) {
    var families = {};
    Object.keys(designs).forEach(function (key) {
      var cfg = designs[key];
      if (cfg.nameFont) families[cfg.nameFont] = true;
      if (cfg.messageFont) families[cfg.messageFont] = true;
    });
    var unique = Object.keys(families);
    if (!unique.length) return;

    var fontKey = unique.slice().sort().join('|');
    if (fontKey === loadedFontKey) return;
    loadedFontKey = fontKey;

    var params = unique
      .map(function (family) {
        return 'family=' + encodeURIComponent(family).replace(/%20/g, '+') + ':wght@400;700';
      })
      .join('&');

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?' + params + '&display=swap';
    document.head.appendChild(link);
  }

  function preloadFonts(cfg, callback) {
    if (!document.fonts || !document.fonts.load) {
      callback();
      return;
    }
    var specs = [];
    if (cfg.nameFont) specs.push('400 100px "' + cfg.nameFont + '"');
    if (cfg.nameFont && cfg.nameBold) specs.push('700 100px "' + cfg.nameFont + '"');
    if (cfg.messageFont) specs.push('400 100px "' + cfg.messageFont + '"');
    if (!specs.length) {
      callback();
      return;
    }
    var promises = specs.map(function (spec) {
      try {
        return document.fonts.load(spec);
      } catch (e) {
        return Promise.resolve();
      }
    });
    Promise.all(promises).then(callback, callback);
  }

  function fitFontSize(ctx, text, maxWidth, startSize, weight, family, minSize) {
    var size = startSize;
    ctx.font = weight + ' ' + size + 'px ' + family;
    while (size > minSize && ctx.measureText(text).width > maxWidth) {
      size -= 2;
      ctx.font = weight + ' ' + size + 'px ' + family;
    }
    return size;
  }

  // line1 tightened further to 0.60 (was 1.55 originally, then 0.93) and name
  // tightened by 35% to 1.01 (was 1.55), both per Susie's request - name's
  // line height is what sets the gap between Name and Message line 1 (it's
  // the vertical space the Name row itself takes up before the next row
  // starts), while line1's sets the gap between Message line 1 and line 2,
  // same as before. Reducing either only SHRINKS the total text block, which
  // is vertically centred within the design's fixed safe_top/safe_bottom
  // band - it can only free up more room at the bottom, never push text
  // further down or off the label, so tightening these is always safe on
  // that front. A design can still override either for itself via its own
  // "Name line height" / "Message line 1 line height" metaobject field.
  //
  // Branded Logo designs specifically get a slightly larger Name line
  // height (see snippets/personalisation-fields.liquid's nameLineHeight
  // override, 1.3 by default there unless a design overrides it in Admin) -
  // that's what actually governs the Company Name/Message gap on Corporate
  // Branding now; this constant remains the tighter default for every
  // other, non-branded design.
  var DEFAULT_LINE_HEIGHT = { age: 1.25, name: 1.01, line1: 0.6, line2: 1.35 };

  function lineHeightFor(role, cfg) {
    var override =
      role === 'name'
        ? cfg.nameLineHeight
        : role === 'age'
        ? cfg.ageLineHeight
        : role === 'line1'
        ? cfg.line1LineHeight
        : cfg.line2LineHeight;
    return override || DEFAULT_LINE_HEIGHT[role];
  }

  function roleStyle(role, cfg) {
    if (role === 'name') {
      return {
        font: cfg.nameFont,
        size: cfg.nameFontSize,
        color: cfg.nameColor,
        weight: cfg.nameBold ? '700' : '400',
        fallbackFamily: 'Georgia, serif'
      };
    }
    return {
      font: cfg.messageFont,
      size: cfg.messageFontSize,
      color: role === 'age' ? cfg.ageColor : cfg.messageColor,
      weight: '400',
      fallbackFamily: 'cursive'
    };
  }

  function drawLineGroup(ctx, width, height, cfg, lines, safeTopFrac, safeBottomFrac, disableCentering) {
    if (!lines.length) return;

    var top = height * safeTopFrac;
    var bottom = height * safeBottomFrac;
    var bandHeight = Math.max(bottom - top, 0);
    var maxWidth = width * (1 - cfg.marginX * 2);
    var spacingMultiplier = cfg.lineSpacing || 1;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    var prepared = lines.map(function (line) {
      var isName = line.role === 'name';
      var style = roleStyle(line.role, cfg);

      var family = "'" + style.font + "', " + style.fallbackFamily;
      var weight = style.weight;
      var color = style.color;
      var text = isName && cfg.nameCaps ? line.text.toUpperCase() : line.text;

      var size;
      if (isName) {
        var baseSize = style.size || height * 0.115;
        size = fitFontSize(ctx, text, maxWidth, baseSize, weight, family, 14);
      } else {
        size = style.size || 75;
      }

      return {
        text: text,
        family: family,
        weight: weight,
        color: color,
        size: size,
        lineHeight: size * lineHeightFor(line.role, cfg) * spacingMultiplier
      };
    });

    var totalHeight = prepared.reduce(function (sum, line) {
      return sum + line.lineHeight;
    }, 0);

    // Normally the whole group is centred vertically within its box (top to
    // bottom of this band) - see the Math.max(...) / 2 offset below. When a
    // design has "Remove Automatic Centre of Name" ticked in Admin (Susie's
    // Sep 30 request - a per-design opt-in, off by default for every design,
    // new and old, so nothing changes unless it's explicitly ticked), the
    // Name is anchored to the TOP of its box instead, so the Name safe-area
    // fields act as a direct position rather than a centred one - no more
    // "moving one edge only shifts it by half" surprise.
    var cursor = disableCentering ? top : top + Math.max(bandHeight - totalHeight, 0) / 2;

    prepared.forEach(function (line) {
      var centerY = cursor + line.lineHeight / 2;
      cursor += line.lineHeight;

      ctx.font = line.weight + ' ' + line.size + 'px ' + line.family;
      ctx.fillStyle = line.color;
      ctx.fillText(line.text, width / 2, centerY);
    });
  }

  function drawTextLines(ctx, width, height, cfg, values) {
    var nameGroup = [];
    if (values.age) nameGroup.push({ text: values.age, role: 'age' });
    if (values.name) nameGroup.push({ text: values.name, role: 'name' });

    var messageGroup = [];
    if (values.line1) messageGroup.push({ text: values.line1, role: 'line1' });
    if (values.line2) messageGroup.push({ text: values.line2, role: 'line2' });

    if (cfg.nameSafeBottom != null && cfg.messageSafeTop != null) {
      // The centering-override only applies here, once a design has its own
      // independent Name/Message zones (Name zone end + Message zone start
      // both set in Admin) - exactly the same split already used for any
      // design needing fine position control. A design still using the
      // single combined block below has no separate Name box to anchor, so
      // the tick box has no effect there - split the design's zones first.
      drawLineGroup(ctx, width, height, cfg, nameGroup, cfg.safeTop, cfg.nameSafeBottom, cfg.nameDisableCentering);
      drawLineGroup(ctx, width, height, cfg, messageGroup, cfg.messageSafeTop, cfg.safeBottom);
    } else {
      drawLineGroup(ctx, width, height, cfg, nameGroup.concat(messageGroup), cfg.safeTop, cfg.safeBottom);
    }
  }

  // Logo occupies a fixed band above the company-name/message text band
  // (which is controlled per-design by safe_top/safe_bottom, same fields as
  // every other design). Kept as simple constants rather than new
  // metaobject fields since all four Branded Logo designs share the same
  // layout. Used both as the logo's default/reset position (computeContainRect
  // below) and as the area the customer can freely drag/resize the logo
  // within (they aren't hard-clamped to it - see the pointer handlers in
  // initBlock()). The customer's uploaded photo (Photo Labels designs) uses
  // this exact same mechanism, just seeded from that design's own
  // photo_safe_top / photo_safe_bottom / margin_x metaobject fields instead
  // of these fixed constants - see getOrSeedPhotoRect() in initBlock().
  var LOGO_SAFE_TOP = 0.06;
  var LOGO_SAFE_BOTTOM = 0.58;
  var LOGO_MARGIN_X = 0.11;

  function sourceWidth(source) {
    return source.naturalWidth || source.width;
  }

  function sourceHeight(source) {
    return source.naturalHeight || source.height;
  }

  // Computes the "contain"-fit box (never cropped) for a draggable/
  // resizable image - the Branded Logo, or a Photo Labels design's uploaded
  // photo - within a safe area given as top/bottom/margin-x fractions of
  // the label. Used to seed where the image starts out the moment it's
  // uploaded (or re-uploaded), before the customer drags/resizes it
  // themselves. Shared by both features: Branded Logo callers pass the
  // fixed LOGO_SAFE_TOP/LOGO_SAFE_BOTTOM/LOGO_MARGIN_X constants above,
  // Photo Labels callers pass that design's own photo_safe_top/
  // photo_safe_bottom/margin_x metaobject values.
  function computeContainRect(source, width, height, topFrac, bottomFrac, marginXFrac) {
    var top = height * topFrac;
    var bottom = height * bottomFrac;
    var marginX = width * marginXFrac;
    var boxWidth = Math.max(width - marginX * 2, 1);
    var boxHeight = Math.max(bottom - top, 1);
    var sw = sourceWidth(source);
    var sh = sourceHeight(source);
    var scale = Math.min(boxWidth / sw, boxHeight / sh);
    var drawWidth = sw * scale;
    var drawHeight = sh * scale;
    var dx = (width - drawWidth) / 2;
    var dy = top + (boxHeight - drawHeight) / 2;
    return { x: dx, y: dy, w: drawWidth, h: drawHeight };
  }

  // Draws a draggable/resizable image (the Branded Logo, or a Photo Labels
  // design's uploaded photo) at its current rect. On the interactive
  // (visible, on-screen) canvas only, also draws a dashed outline so the
  // customer can see they can drag to reposition it - this overlay is never
  // drawn on the hidden export canvas, so it never ends up in the
  // printed/exported label.
  //
  // The round resize handle itself is separate from that outline (see
  // showHandle below) - Susie reported (26 Sep, second round) that it sat
  // permanently visible in the corner of the live preview and read as a
  // stray dot on the picture even when the customer wasn't trying to resize
  // anything. First attempt hid it completely until the customer's pointer
  // found it (hover/drag only) - but Susie then reported the logo "can no
  // longer be enlarged": the resize itself was never actually broken (the
  // corner's hit-area is unchanged and still works the instant a drag starts
  // there), the real problem was that with NOTHING drawn there at all, a
  // customer had no way to discover where that corner even was, so they
  // never found it to start dragging from. Fixed by never hiding the handle
  // completely: it now always shows at a faint, low-opacity ghost (barely
  // there, nothing like the original "stray dot") so there's always
  // something to spot and aim for, and becomes fully solid the moment the
  // customer's pointer finds it or they start dragging it - see
  // HANDLE_IDLE_OPACITY below and showHandle in redraw().
  var RESIZE_HANDLE_RADIUS = 26;
  var HANDLE_IDLE_OPACITY = 0.35;

  function drawImageWithHandle(ctx, source, rect, interactive, showHandle) {
    ctx.drawImage(source, rect.x, rect.y, rect.w, rect.h);
    if (!interactive) return;
    // Grey, thin dashed outline (Susie's request, 26 Sep: the previous dark
    // teal outline at 4px width read as a "large black line" and felt
    // alarming around an uploaded photo/logo - lightened to a subtler grey
    // and thinned down so it just quietly indicates the draggable area).
    ctx.save();
    ctx.strokeStyle = 'rgba(150, 150, 150, 0.6)';
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 8]);
    ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = showHandle ? 1 : HANDLE_IDLE_OPACITY;
    ctx.fillStyle = '#103948';
    ctx.beginPath();
    ctx.arc(rect.x + rect.w, rect.y + rect.h, RESIZE_HANDLE_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.restore();
  }

  // Sets the canvas's actual pixel buffer to LOGICAL_WIDTH/HEIGHT * the
  // supersampling factor above, only touching it (which resets/clears the
  // canvas) when it isn't already that size.
  //
  // Updated 4 Oct 2026 (V81, Susie: "the print looks all disjointed" in the
  // live preview). The hidden EXPORT canvas still renders at the full
  // supersampled size, so the printed file is unchanged. The VISIBLE
  // preview, though, used to be rendered at 3000x2000 and then squashed by
  // the browser into a box only ~300-400px wide - roughly a 10x shrink,
  // which breaks the thin strokes of script fonts (Pinyon etc.) into
  // jagged fragments. It now renders at exactly its on-screen size x the
  // screen's pixel ratio, so the browser draws the text natively at the
  // size it is shown - smooth and joined-up. Falls back to the old full
  // size if the canvas isn't laid out yet (e.g. hidden); a ResizeObserver
  // in initBlock() redraws it at the right size once it is visible.
  function canvasScaleFor(canvas) {
    if (canvas.hasAttribute('data-personalisation-export-canvas')) return RENDER_SCALE;
    var cssW = canvas.clientWidth || 0;
    if (!cssW) return RENDER_SCALE;
    var s = (cssW * (window.devicePixelRatio || 1)) / LOGICAL_WIDTH;
    return Math.min(Math.max(s, 0.1), RENDER_SCALE);
  }

  function ensureCanvasResolution(canvas) {
    var scale = canvasScaleFor(canvas);
    var targetW = Math.round(LOGICAL_WIDTH * scale);
    var targetH = Math.round(LOGICAL_HEIGHT * scale);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }
    return targetW / LOGICAL_WIDTH;
  }

  // Shrinks a large canvas onto a smaller one in halving steps (each step
  // at most 2x), which keeps thin lines smooth - a single big jump is what
  // made the old preview look broken up. Used by "View larger preview".
  function stepDownDraw(source, target) {
    var src = source;
    var w = source.width;
    var h = source.height;
    while (w / 2 > target.width) {
      var tmp = document.createElement('canvas');
      tmp.width = Math.round(w / 2);
      tmp.height = Math.round(h / 2);
      var tctx = tmp.getContext('2d');
      if ('imageSmoothingQuality' in tctx) tctx.imageSmoothingQuality = 'high';
      tctx.drawImage(src, 0, 0, tmp.width, tmp.height);
      src = tmp;
      w = tmp.width;
      h = tmp.height;
    }
    var ctx = target.getContext('2d');
    if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.drawImage(src, 0, 0, target.width, target.height);
  }

  // "Photo only" (V82, Susie's request 4 Oct 2026) - a Photo Labels
  // customer can tick "Photo only" so their photo IS the label, with no
  // wording. The photo is scaled to reach the top and bottom edges and
  // centred. A wide photo (panorama) runs off the sides and can be dragged
  // left/right (offsetX, clamped so it always covers the label). A tall or
  // square photo doesn't reach the sides, so the gaps are filled with a
  // soft, blurred, enlarged copy of the same photo - made by shrinking it
  // to a tiny canvas and stretching it back up, which blurs it the same way
  // in every browser (ctx.filter isn't supported everywhere).
  function photoOnlyLayout(img, width, height, offsetX) {
    var sw = sourceWidth(img);
    var sh = sourceHeight(img);
    var scale = height / sh;
    var dw = sw * scale;
    var dx = (width - dw) / 2;
    var pannable = dw > width + 1;
    if (pannable) {
      var minX = width - dw;
      dx = Math.min(Math.max(dx + (offsetX || 0), minX), 0);
    }
    return { x: dx, y: 0, w: dw, h: height, pannable: pannable, centreX: (width - dw) / 2 };
  }

  function drawPhotoOnly(ctx, width, height, img, offsetX) {
    var layout = photoOnlyLayout(img, width, height, offsetX);
    if (!layout.pannable) {
      var sw = sourceWidth(img);
      var sh = sourceHeight(img);
      var tiny = document.createElement('canvas');
      tiny.width = 48;
      tiny.height = Math.max(1, Math.round(48 * height / width));
      var tctx = tiny.getContext('2d');
      var cover = Math.max(tiny.width / sw, tiny.height / sh);
      tctx.drawImage(img, (tiny.width - sw * cover) / 2, (tiny.height - sh * cover) / 2, sw * cover, sh * cover);
      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(tiny, -width * 0.04, -height * 0.04, width * 1.08, height * 1.08);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }
    ctx.drawImage(img, layout.x, layout.y, layout.w, layout.h);
  }

  // Returns a Promise that resolves once this canvas has actually finished
  // painting (or been skipped as stale) - redraw() below and the Add to
  // Cart submit handler both rely on this to know the canvas is genuinely
  // up to date before, respectively, resolving their own combined promise
  // or capturing the export PNG, rather than assuming a same-tick paint
  // that (because of the async font/image loading below) hasn't actually
  // happened yet.
  function drawLabel(canvas, cfg, values, opts) {
    return new Promise(function (resolve) {
      var renderScale = ensureCanvasResolution(canvas);
      var ctx = canvas.getContext('2d');
      // Everything below draws in the fixed 1500x1000 logical space, same
      // as always - this transform maps it onto the physical buffer set up
      // above (full supersampled size for the export canvas, on-screen size
      // for the visible preview). Set fresh (not accumulated) on every draw,
      // so repeated redraws never compound.
      ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
      var width = LOGICAL_WIDTH;
      var height = LOGICAL_HEIGHT;

      if (!cfg) {
        ctx.clearRect(0, 0, width, height);
        resolve();
        return;
      }

      loadImage(cfg.image, function (img) {
        preloadFonts(cfg, function () {
          // Loading the design image and its fonts both happen asynchronously,
          // so a customer switching categories/designs quickly can leave more
          // than one of these callbacks in flight at once. Without a check
          // here, whichever one happens to finish LAST wins and paints over
          // the canvas - even for a design the customer has since switched
          // away from. isStale() lets the caller (redraw() below) mark every
          // draw request as superseded the moment a newer one starts, so only
          // the most recent one is ever allowed to actually paint.
          if (opts && opts.isStale && opts.isStale()) {
            resolve();
            return;
          }

          // Everything below is wrapped defensively (added 22 Sep): if any
          // drawing step throws - a bad font metric, an unexpected image
          // state, anything unforeseen - this promise still resolves rather
          // than hanging forever. A hung promise here would silently stop
          // Add to Cart from ever completing, since the submit handler
          // below waits on drawLabel() finishing before letting the real
          // submit through - which would look exactly like "clicking Add to
          // Cart does nothing", with a retry sometimes succeeding once
          // fonts/images have finished loading in the background. This
          // guards against that whole class of failure without changing
          // anything about the normal, successful draw.
          try {
            ctx.clearRect(0, 0, width, height);

            if (cfg.isPhotoUpload && values.photoOnly && values.photo) {
              drawPhotoOnly(ctx, width, height, values.photo, values.photoOffset || 0);
              resolve();
              return;
            }

            if (img) {
              var scale = Math.max(width / img.naturalWidth, height / img.naturalHeight);
              var drawWidth = img.naturalWidth * scale;
              var drawHeight = img.naturalHeight * scale;
              var dx = (width - drawWidth) / 2;
              var dy = (height - drawHeight) / 2;
              ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
            }

            if (cfg.isBranded) {
              if (values.logo) {
                var logoRect = (opts && opts.getLogoRect)
                  ? opts.getLogoRect(values.logo)
                  : computeContainRect(values.logo, width, height, LOGO_SAFE_TOP, LOGO_SAFE_BOTTOM, LOGO_MARGIN_X);
                drawImageWithHandle(ctx, values.logo, logoRect, !!(opts && opts.interactive), !!(opts && opts.showHandle));
              }
              // Customer-chosen colours (Corporate Branding only - one for the
              // name/tagline line, one for the message; see the text colour
              // block in initBlock() below) override the design's own colours.
              var textCfg = Object.assign({}, cfg);
              if (values.nameColour) textCfg.nameColor = values.nameColour;
              if (values.messageColour) {
                textCfg.messageColor = values.messageColour;
                textCfg.ageColor = values.messageColour;
              }
              drawTextLines(ctx, width, height, textCfg, {
                age: '',
                name: values.companyName || '',
                line1: values.line1,
                line2: values.line2
              });
            } else {
              if (cfg.isPhotoUpload && values.photo) {
                // Drawn with the same draggable/resizable machinery as the
                // Branded Logo (see the file header comment) - "contain"-fit
                // within the design's photo_safe_top/photo_safe_bottom/
                // margin_x band, not cropped/clipped to it, so the customer
                // can freely resize it larger or smaller, same as a logo.
                var photoRect = (opts && opts.getPhotoRect)
                  ? opts.getPhotoRect(values.photo)
                  : computeContainRect(values.photo, width, height, cfg.photoSafeTop, cfg.photoSafeBottom, cfg.marginX);
                drawImageWithHandle(ctx, values.photo, photoRect, !!(opts && opts.interactive), !!(opts && opts.showHandle));
              }
              drawTextLines(ctx, width, height, cfg, values);
            }
          } catch (e) {
            // Swallow and fall through to resolve() below regardless - see
            // comment above.
          }
          resolve();
        });
      });
    });
  }

  // Draws a customer's "Upload your own ready-made design" file directly onto
  // the live preview canvas - contain-fit on a white background, centred,
  // with no text or logo compositing - since that file is what actually gets
  // printed, exactly as supplied. This is the ONLY preview shown while that
  // upload is active (see redraw() below); the normal design-swatch
  // compositing (drawLabel()) is skipped entirely, not layered underneath.
  function drawOwnDesignPreview(canvas, img) {
    return new Promise(function (resolve) {
      var renderScale = ensureCanvasResolution(canvas);
      var ctx = canvas.getContext('2d');
      ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
      var width = LOGICAL_WIDTH;
      var height = LOGICAL_HEIGHT;
      try {
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        var scale = Math.min(width / img.naturalWidth, height / img.naturalHeight);
        var drawWidth = img.naturalWidth * scale;
        var drawHeight = img.naturalHeight * scale;
        var dx = (width - drawWidth) / 2;
        var dy = (height - drawHeight) / 2;
        ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
      } catch (e) {
        // Swallow, same defensive pattern as drawLabel() above - a failed
        // preview draw should never block anything else on the page.
      }
      resolve();
    });
  }

  // Exports whatever is CURRENTLY painted on the export canvas (or the
  // visible one, as a fallback) straight to the hidden Label Preview file
  // input. Called from redraw() itself below (added 22 Sep) so the file
  // input is kept continuously up to date as the customer types/picks a
  // design, rather than only being filled in at submit time - see the
  // submit listener's comment near the bottom of initBlock() for why that
  // used to happen at submit time and what went wrong with it.
  function exportToFileInput(fileInput, sourceCanvas) {
    if (!fileInput || typeof DataTransfer === 'undefined') return;
    try {
      var dataURL = sourceCanvas.toDataURL('image/png');
      var blob = dataURLtoBlob(dataURL);
      var file = new File([blob], 'label-preview.png', { type: 'image/png' });
      var transfer = new DataTransfer();
      transfer.items.add(file);
      fileInput.files = transfer.files;
    } catch (e) {
      // Export failed for some reason - leave fileInput as whatever it last
      // held (or empty). Never fatal: the typed-out text properties (and,
      // for Branded Logo, the original uploaded logo file) still reach the
      // order regardless - the exported preview image is a bonus, not a
      // dependency (see the file header comment).
    }
  }

  function dataURLtoBlob(dataURL) {
    var parts = dataURL.split(',');
    var mimeMatch = parts[0].match(/:(.*?);/);
    var mime = mimeMatch ? mimeMatch[1] : 'image/png';
    var binary = atob(parts[1]);
    var array = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  }

  function initBlock(block) {
    var canvas = block.querySelector('[data-personalisation-canvas]');
    if (!canvas) return;

    // Kept permanently at the same fixed 1500x1000 resolution as the
    // visible canvas, and always redrawn WITHOUT the drag/resize handle
    // overlay - this is what actually gets exported for the order, so the
    // on-screen interaction chrome never ends up in the printed label.
    // Falls back to exporting the visible canvas if this second one isn't
    // present (e.g. an older cached page).
    var exportCanvas = block.querySelector('[data-personalisation-export-canvas]');

    var configScript = block.querySelector('[data-personalisation-designs]');
    var designs = {};
    if (configScript) {
      try {
        designs = JSON.parse(configScript.textContent);
      } catch (e) {
        designs = {};
      }
    }

    ensureFontsLoaded(designs);

    var fileInput = block.querySelector('[data-personalisation-preview-input]');
    var labelRefInput = block.querySelector('[data-personalisation-label-ref]');
    var designRadios = Array.prototype.slice.call(
      block.querySelectorAll('input[name="properties[Label Design]"]')
    );
    var nameInput = block.querySelector('input[name="properties[Name]"]');
    var ageInput = block.querySelector('input[name="properties[Age / Date]"]');
    var line1Input = block.querySelector('input[name="properties[Message Line 1]"]');
    var line2Input = block.querySelector('input[name="properties[Message Line 2]"]');
    // Hidden (and cleared/disabled) for a "Photo upload" design - see
    // redraw() below and the data-message-line2-field comment in
    // snippets/personalisation-fields.liquid.
    var line2Field = block.querySelector('[data-message-line2-field]');
    var companyNameInput = block.querySelector('input[name="properties[Company Name]"]');
    // Branded Logo has its own single Message input (same cart property
    // name as the standard Message - line 1 field above, so staff see one
    // consistent property regardless of category, but a distinct element -
    // see data-personalisation-branded-message in
    // snippets/personalisation-fields.liquid). Only one of the two is ever
    // enabled at a time (see setFieldMode below) so a plain
    // querySelector('input[name="properties[Message Line 1]"]') can't be
    // reused for both - it would only ever find the first (standard) one.
    var brandedMessageInput = block.querySelector('[data-personalisation-branded-message]');
    var logoInput = block.querySelector('[data-personalisation-logo-input]');
    var removeBgCheckbox = block.querySelector('[data-personalisation-remove-bg]');
    // "Photo upload" designs (Shopify Admin > Content > Metaobjects > Label
    // Design > "Photo upload" checkbox, e.g. the Photo Labels category) -
    // this is per-DESIGN, not per-category, so its visibility is driven by
    // which design radio is currently checked (see redraw() below), unlike
    // the standard/branded split above which is driven by category.
    var photoUploadField = block.querySelector('[data-photo-upload-field]');
    var photoInput = block.querySelector('[data-personalisation-photo-input]');
    // V82 "Photo only" tick box and its two notes - see drawPhotoOnly().
    var photoOnlyCheckbox = block.querySelector('[data-personalisation-photo-only]');
    // V84 (Susie's request, 5 Oct 2026): the "Photo only" tick box is now
    // fronted by two boxes, each with its own "Choose file" button, copying
    // the Corporate Branding upload-box layout - see the data-photo-mode-group
    // comment in snippets/personalisation-fields.liquid. photoOnlyCheckbox
    // above still exists (visually hidden) and still drives every bit of
    // existing Photo only logic untouched; these two trigger buttons just
    // keep it in sync with whichever one the customer actually uploads
    // through (see the click listener further down).
    var photoModeTriggers = Array.prototype.slice.call(block.querySelectorAll('[data-photo-mode-trigger]'));
    var photoQualityNote = block.querySelector('[data-personalisation-photo-quality]');
    var photoPanHint = block.querySelector('[data-personalisation-photo-pan-hint]');
    var photoOnlyOffset = 0;
    var standardFields = block.querySelector('[data-fields-standard]');
    var brandedFields = block.querySelector('[data-fields-branded]');
    // Shared by the Branded Logo and Photo Labels drag/resize interactions
    // (only one is ever relevant at a time - see activeImageKind() below).
    var resizeHint = block.querySelector('[data-personalisation-resize-hint]');

    // "Upload your own ready-made design" - see the file header comment.
    var ownDesignInput = block.querySelector('[data-personalisation-owndesign-input]');

    // Branded Logo starts hidden (see snippets/personalisation-fields.liquid),
    // so its Message input starts disabled - disabled form fields are
    // excluded from FormData automatically, which is what keeps it from
    // colliding with the standard Message - line 1 field's same-named input
    // on submit. setFieldMode() below flips this whenever the customer
    // switches category.
    if (brandedMessageInput) brandedMessageInput.disabled = true;

    // Corporate Branding text colours (Susie's requests, 2 Oct 2026). Two
    // rows of five round swatches (Black, White, Cream, Red, Dark Blue) -
    // one for the "Name, Tagline or Logo Message" line and one for the
    // Message - so each line can have its own colour (see the Text colours
    // comment in snippets/personalisation-fields.liquid). Until the customer
    // picks one, each row follows the background automatically (Black on
    // light backgrounds, Cream on dark ones - the same look Corporate
    // Branding always had). A colour that wouldn't show up on the chosen
    // background (contrast below 2:1, e.g. Dark Blue on the Dark Blue label,
    // Black on Dark Blue, or White/Cream on White/Vintage) is refused with a
    // pop-up asking for another colour. If the customer changes background
    // and a chosen colour would then clash, that row switches to the
    // automatic colour and the pop-up says so. Radios are disabled unless
    // Corporate Branding is active and no ready-made design is uploaded, so
    // nothing else ever sends these properties.
    var TEXT_COLOUR_GROUP_LABELS = { name: 'name, tagline or logo message', message: 'message' };
    var textColourGroups = Array.prototype.slice.call(block.querySelectorAll('[data-text-colour-group]')).map(function (fieldset) {
      return {
        key: fieldset.getAttribute('data-text-colour-group'),
        radios: Array.prototype.slice.call(fieldset.querySelectorAll('[data-text-colour]')),
        touched: false,
        last: null
      };
    });
    var textColourRadios = textColourGroups.reduce(function (all, group) {
      return all.concat(group.radios);
    }, []);
    var textColourNotice = block.querySelector('[data-text-colour-notice]');
    var textColourNoticeMessage = block.querySelector('[data-text-colour-notice-message]');
    var textColourNoticeClose = block.querySelector('[data-text-colour-notice-close]');
    var BRANDED_BACKGROUND_HEX = { 'White': '#ffffff', 'Vintage': '#fffdf9', 'Black': '#000000', 'Dark Blue': '#00006c' };
    textColourRadios.forEach(function (radio) {
      radio.disabled = true;
    });

    function hexToRgb(hex) {
      var m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
      if (!m) return null;
      var n = parseInt(m[1], 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }

    function relativeLuminance(hex) {
      var rgb = hexToRgb(hex);
      if (!rgb) return null;
      var c = rgb.map(function (v) {
        v = v / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
    }

    function contrastRatio(hexA, hexB) {
      var a = relativeLuminance(hexA);
      var b = relativeLuminance(hexB);
      if (a === null || b === null) return 21;
      return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
    }

    function currentBackgroundHex() {
      var checked = designRadios.filter(function (radio) {
        return radio.checked;
      })[0];
      if (!checked) return null;
      var cfg = designs[checked.value];
      if (!cfg || !cfg.isBranded) return null;
      if (BRANDED_BACKGROUND_HEX[checked.value]) return BRANDED_BACKGROUND_HEX[checked.value];
      // Unknown/renamed background: assume it's the opposite of the
      // design's own text colour, which was chosen to contrast with it.
      var nameLum = relativeLuminance(cfg.nameColor);
      return nameLum !== null && nameLum > 0.4 ? '#000000' : '#ffffff';
    }

    function groupCheckedRadio(group) {
      return group.radios.filter(function (radio) {
        return radio.checked;
      })[0] || null;
    }

    function groupColourHex(key) {
      var group = textColourGroups.filter(function (g) {
        return g.key === key;
      })[0];
      var radio = group ? groupCheckedRadio(group) : null;
      return radio ? radio.getAttribute('data-text-colour') : null;
    }

    function selectGroupColour(group, name) {
      var picked = null;
      group.radios.forEach(function (radio) {
        radio.checked = radio.value === name;
        if (radio.checked) picked = radio;
      });
      group.last = picked;
    }

    function textColourClashes(radio) {
      var bg = currentBackgroundHex();
      return !!(radio && bg && contrastRatio(radio.getAttribute('data-text-colour'), bg) < 2);
    }

    function automaticTextColourName() {
      var lum = relativeLuminance(currentBackgroundHex());
      return lum !== null && lum < 0.4 ? 'Cream' : 'Black';
    }

    function showTextColourNotice(message) {
      if (!textColourNotice) return;
      if (textColourNoticeMessage) textColourNoticeMessage.textContent = message;
      textColourNotice.hidden = false;
      if (textColourNoticeClose) textColourNoticeClose.focus({ preventScroll: true });
    }

    function hideTextColourNotice() {
      if (textColourNotice) textColourNotice.hidden = true;
    }

    if (textColourNoticeClose) textColourNoticeClose.addEventListener('click', hideTextColourNotice);
    if (textColourNotice) {
      textColourNotice.addEventListener('click', function (event) {
        if (event.target === textColourNotice) hideTextColourNotice();
      });
      textColourNotice.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') hideTextColourNotice();
      });
    }

    // Keeps each line's colour sensible for the current background. Called
    // at the start of every redraw() while Corporate Branding is active -
    // cheap, and does nothing unless something actually needs changing.
    function syncTextColourForBackground() {
      if (!textColourGroups.length || !isBrandedActive() || !currentBackgroundHex()) return;
      var switched = [];
      var auto = automaticTextColourName();
      textColourGroups.forEach(function (group) {
        var current = groupCheckedRadio(group);
        if (!current || !group.touched) {
          if (!current || current.value !== auto) selectGroupColour(group, auto);
          return;
        }
        if (textColourClashes(current)) {
          switched.push(current.value + ' on your ' + TEXT_COLOUR_GROUP_LABELS[group.key]);
          selectGroupColour(group, auto);
        }
      });
      if (switched.length) {
        showTextColourNotice(switched.join(' and ') + ' would not show up on this background, so we have changed it to ' + auto + '. You can choose a different colour if you prefer.');
      }
    }

    textColourGroups.forEach(function (group) {
      group.radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          if (textColourClashes(radio)) {
            var fallback = group.last && group.last !== radio ? group.last.value : automaticTextColourName();
            selectGroupColour(group, fallback);
            showTextColourNotice(radio.value + ' is too close to the colour of your label background, so your ' + TEXT_COLOUR_GROUP_LABELS[group.key] + ' would not show up. Please choose a different colour.');
          } else {
            group.touched = true;
            group.last = radio;
          }
          redraw();
        });
      });
    });

    function syncTextColourEnabled() {
      if (logoOnlyCheckbox) logoOnlyCheckbox.disabled = !isBrandedActive();
      var enable = isBrandedActive() && !ownDesignActive() && !logoOnlyActive();
      textColourRadios.forEach(function (radio) {
        radio.disabled = !enable;
      });
    }

    // "Logo only - no text, enlarged for impact" (Susie, 2 Oct 2026). When
    // ticked: the Name/Tagline and Message boxes and the Text colours
    // section are hidden, cleared and disabled (so nothing is sent for
    // them), the Name/Tagline box stops being required (its
    // data-required-path is removed, so assets/personalisation.js only
    // needs the logo itself), and the logo is re-seeded much larger. Unticking
    // puts everything back. Sent as properties[Logo Only] = Yes.
    var logoOnlyCheckbox = block.querySelector('[data-personalisation-logo-only]');
    var logoOnlyHideEls = Array.prototype.slice.call(block.querySelectorAll('[data-logo-only-hide]'));

    function logoOnlyActive() {
      return !!(logoOnlyCheckbox && logoOnlyCheckbox.checked && !logoOnlyCheckbox.disabled);
    }

    function applyLogoOnly() {
      var on = logoOnlyActive();
      logoOnlyHideEls.forEach(function (el) {
        el.hidden = on;
      });
      if (companyNameInput) {
        if (on) {
          companyNameInput.value = '';
          companyNameInput.removeAttribute('data-required-path');
          companyNameInput.disabled = true;
          var nameErrorId = companyNameInput.getAttribute('data-error-target');
          var nameError = nameErrorId ? document.getElementById(nameErrorId) : null;
          if (nameError) nameError.classList.add('hidden');
        } else {
          companyNameInput.setAttribute('data-required-path', 'standard');
          companyNameInput.disabled = false;
        }
      }
      if (brandedMessageInput && isBrandedActive()) {
        if (on) brandedMessageInput.value = '';
        brandedMessageInput.disabled = on;
      }
      // Re-seed the logo at the size that suits the new layout.
      logoTransform = null;
      syncTextColourEnabled();
    }

    if (logoOnlyCheckbox) {
      logoOnlyCheckbox.disabled = true;
      logoOnlyCheckbox.addEventListener('change', function () {
        applyLogoOnly();
        redraw();
      });
    }

    // Custom-styled file inputs (currently just the Company Logo upload):
    // the real <input type="file"> stays in the DOM and fully functional
    // (it's what the visible "Choose file" button's `for` points at, so it
    // opens the native file picker exactly as before, and it's still what
    // gets validated and submitted) - it's only visually hidden, with a
    // styled button and this filename text standing in for the browser's
    // own file-input chrome. Written generically off data-file-input /
    // data-file-name so any other file field can opt into the same look
    // later just by using the same markup.
    Array.prototype.slice.call(block.querySelectorAll('[data-file-input]')).forEach(function (input) {
      var wrapper = input.closest('.personalisation-fields__file-field');
      var nameEl = wrapper ? wrapper.querySelector('[data-file-name]') : null;
      if (!nameEl) return;
      var defaultText = nameEl.textContent;
      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        nameEl.textContent = file ? file.name : defaultText;
      });
    });

    var rawLogoImage = null; // the uploaded file, decoded, background untouched
    var processedLogoCanvas = null; // same image with a near-white background stripped

    var rawPhotoImage = null; // customer's uploaded photo (Photo Labels designs), decoded

    var rawOwnDesignImage = null; // "Upload your own ready-made design" file, decoded - shown as-is on the live preview canvas, never composited

    // Which of the two the preview (and final exported PNG) actually uses -
    // the customer can untick "Remove background automatically" for a logo
    // that's deliberately designed with a solid background.
    function currentLogoSource() {
      if (!rawLogoImage) return null;
      if (removeBgCheckbox && !removeBgCheckbox.checked) return rawLogoImage;
      return processedLogoCanvas || rawLogoImage;
    }

    function isBrandedActive() {
      return !!(brandedFields && !brandedFields.hidden);
    }

    // True once the customer has actually chosen a file in "Upload your own
    // ready-made design" - this box has no separate "mode" any more (see the
    // file header comment), it's just always visible, so whether it's
    // "active" is purely a question of whether it has a file in it.
    function ownDesignActive() {
      return !!(ownDesignInput && ownDesignInput.files && ownDesignInput.files.length);
    }

    function resetOwnDesignField() {
      if (ownDesignInput) {
        ownDesignInput.value = '';
        var wrapper = ownDesignInput.closest('.personalisation-fields__file-field');
        var nameEl = wrapper ? wrapper.querySelector('[data-file-name]') : null;
        if (nameEl) nameEl.textContent = 'No file chosen';
      }
      rawOwnDesignImage = null;
    }

    // Keeps the hidden canvas-export file input (fileInput) and the "Upload
    // your own ready-made design" input (ownDesignInput) mutually exclusive,
    // since both submit under the same `properties[_Label Preview]` name -
    // disabled form fields are excluded from FormData automatically, so only
    // one of the two is ever actually included on submit. Call this whenever
    // ownDesignInput's own file selection changes (see its 'change'
    // listener below) and once at redraw() time as a cheap safety net.
    function syncOwnDesignExclusivity() {
      if (fileInput) fileInput.disabled = ownDesignActive();
      syncTextColourEnabled();
    }

    // Where the customer's logo currently sits on the label - in the same
    // fixed 1500x1000 "design space" every other drawing calculation uses.
    // Stays null until a logo is uploaded, at which point it's seeded to a
    // sensible centred/contained starting size (see computeContainRect
    // above); a fresh upload always resets it back to that seed. Shared by
    // both canvases, so the exported/printed label always exactly matches
    // whatever the customer arranged on screen.
    var logoTransform = null;

    // Same idea, for a Photo Labels design's uploaded photo - see the file
    // header comment. Kept as a separate variable (rather than reusing
    // logoTransform) since, in principle, stale state from one shouldn't
    // leak into the other even though only one is ever active on screen at
    // once.
    var photoTransform = null;

    function getOrSeedLogoRect(source) {
      if (!logoTransform) {
        // "Logo only" (see applyLogoOnly) seeds the logo much larger -
        // almost the whole label - since there's no text to leave room for.
        logoTransform = logoOnlyActive()
          ? computeContainRect(source, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0.08, 0.92, 0.08)
          : computeContainRect(source, LOGICAL_WIDTH, LOGICAL_HEIGHT, LOGO_SAFE_TOP, LOGO_SAFE_BOTTOM, LOGO_MARGIN_X);
      }
      return logoTransform;
    }

    function getOrSeedPhotoRect(source) {
      if (!photoTransform) {
        var cfg = getCurrentDesign();
        var topFrac = cfg ? cfg.photoSafeTop : 0.1;
        var bottomFrac = cfg ? cfg.photoSafeBottom : 0.75;
        var marginXFrac = cfg ? cfg.marginX : 0.14;
        photoTransform = computeContainRect(source, LOGICAL_WIDTH, LOGICAL_HEIGHT, topFrac, bottomFrac, marginXFrac);
      }
      return photoTransform;
    }

    // Lets a customer, while a Branded Logo design is selected, drag their
    // uploaded logo to reposition it, or drag the handle in its
    // bottom-right corner to resize it (uniformly, so it never looks
    // stretched) - and, while a Photo Labels design is selected, do the
    // exact same thing to their uploaded photo. activeImageKind() below
    // decides which (if either) is relevant at any given moment, so every
    // handler here has zero effect on any other design.
    var IMAGE_RESIZE_HANDLE_HIT = 90;
    var IMAGE_MIN_SIZE = 80;
    var dragState = null;

    // True while the pointer is currently over the resize handle's hit area
    // (or actively dragging it) - see the drawImageWithHandle() file header
    // comment for why: the handle paints fully solid while this is true, and
    // at a faint low-opacity ghost the rest of the time (never fully hidden -
    // see HANDLE_IDLE_OPACITY), so it's always at least visible enough to
    // find and grab.
    var handleHovered = false;

    function pointToLogicalSpace(evt) {
      var rect = canvas.getBoundingClientRect();
      return {
        x: ((evt.clientX - rect.left) / rect.width) * LOGICAL_WIDTH,
        y: ((evt.clientY - rect.top) / rect.height) * LOGICAL_HEIGHT
      };
    }

    function hitTestRect(t, pt) {
      if (!t) return null;
      var handleX = t.x + t.w;
      var handleY = t.y + t.h;
      if (Math.abs(pt.x - handleX) <= IMAGE_RESIZE_HANDLE_HIT && Math.abs(pt.y - handleY) <= IMAGE_RESIZE_HANDLE_HIT) {
        return 'resize';
      }
      if (pt.x >= t.x && pt.x <= t.x + t.w && pt.y >= t.y && pt.y <= t.y + t.h) {
        return 'move';
      }
      return null;
    }

    // Whichever single draggable/resizable image is relevant right now, if
    // any - the Branded Logo while that category is selected, or a Photo
    // Labels design's uploaded photo while one of those is selected. These
    // two are mutually exclusive (Photo Labels designs live in the standard
    // category, never the Branded Logo one), so at most one is ever active.
    function activeImageKind() {
      if (isBrandedActive()) return logoTransform ? 'logo' : null;
      var cfg = getCurrentDesign();
      if (cfg && cfg.isPhotoUpload && photoOnlyActive()) {
        return rawPhotoImage && photoOnlyLayout(rawPhotoImage, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0).pannable ? 'photoOnly' : null;
      }
      if (cfg && cfg.isPhotoUpload) return photoTransform ? 'photo' : null;
      return null;
    }

    function activeImageTransform() {
      var kind = activeImageKind();
      if (kind === 'logo') return logoTransform;
      if (kind === 'photo') return photoTransform;
      return null;
    }

    canvas.addEventListener('pointerdown', function (evt) {
      if (activeImageKind() === 'photoOnly') {
        evt.preventDefault();
        dragState = { kind: 'photoOnly', mode: 'pan', startPt: pointToLogicalSpace(evt), origOffset: photoOnlyOffset };
        canvas.style.cursor = 'grabbing';
        if (canvas.setPointerCapture && evt.pointerId != null) {
          try {
            canvas.setPointerCapture(evt.pointerId);
          } catch (e) {}
        }
        return;
      }
      var kind = activeImageKind();
      var t = activeImageTransform();
      if (!kind || !t) return;
      var pt = pointToLogicalSpace(evt);
      var hit = hitTestRect(t, pt);
      if (!hit) return;
      evt.preventDefault();
      dragState = {
        kind: kind,
        mode: hit,
        startPt: pt,
        orig: { x: t.x, y: t.y, w: t.w, h: t.h }
      };
      if (canvas.setPointerCapture && evt.pointerId != null) {
        try {
          canvas.setPointerCapture(evt.pointerId);
        } catch (e) {}
      }
    });

    canvas.addEventListener('pointermove', function (evt) {
      if (dragState && dragState.kind === 'photoOnly') {
        evt.preventDefault();
        var panPt = pointToLogicalSpace(evt);
        var lay = photoOnlyLayout(rawPhotoImage, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0);
        var maxShift = Math.max(0, (lay.w - LOGICAL_WIDTH) / 2);
        photoOnlyOffset = Math.min(Math.max(dragState.origOffset + (panPt.x - dragState.startPt.x), -maxShift), maxShift);
        redraw();
        return;
      }
      if (!dragState && activeImageKind() === 'photoOnly') {
        canvas.style.cursor = 'grab';
        return;
      }
      if (!dragState) {
        var kind = activeImageKind();
        var t = activeImageTransform();
        if (kind && t) {
          var hoverHit = hitTestRect(t, pointToLogicalSpace(evt));
          canvas.style.cursor = hoverHit === 'resize' ? 'nwse-resize' : hoverHit === 'move' ? 'move' : 'default';
          // Only redraw when the hover state actually flips (not on every
          // pixel of pointer movement) - the handle dot appears the moment
          // the pointer finds it, and disappears the moment it leaves.
          var isHoveringHandle = hoverHit === 'resize';
          if (isHoveringHandle !== handleHovered) {
            handleHovered = isHoveringHandle;
            redraw();
          }
        } else if (canvas.style.cursor) {
          canvas.style.cursor = '';
        }
        return;
      }
      evt.preventDefault();
      var pt = pointToLogicalSpace(evt);
      var dx = pt.x - dragState.startPt.x;
      var dy = pt.y - dragState.startPt.y;
      var t = dragState.kind === 'logo' ? logoTransform : photoTransform;
      if (!t) return;

      if (dragState.mode === 'move') {
        var minX = -t.w * 0.8;
        var maxX = LOGICAL_WIDTH - t.w * 0.2;
        var minY = -t.h * 0.8;
        var maxY = LOGICAL_HEIGHT - t.h * 0.2;
        t.x = Math.min(Math.max(dragState.orig.x + dx, minX), maxX);
        t.y = Math.min(Math.max(dragState.orig.y + dy, minY), maxY);
      } else {
        var newWidth = Math.max(IMAGE_MIN_SIZE, dragState.orig.w + dx);
        var scaleFactor = newWidth / dragState.orig.w;
        t.w = newWidth;
        t.h = Math.max(IMAGE_MIN_SIZE, dragState.orig.h * scaleFactor);
        // Resizing keeps the image horizontally centred on the label
        // automatically, rather than growing from the fixed left edge of
        // the drag and drifting off-centre - most logos/photos read best
        // centred, and this saves an extra manual re-centring drag every
        // time the customer makes it bigger or smaller. (Moving it -
        // dragging its middle, not the corner handle - still works exactly
        // as before, so it can still be placed off-centre on purpose.)
        t.x = (LOGICAL_WIDTH - t.w) / 2;
      }
      redraw();
    });

    function endDrag() {
      // Hide the resize handle again the instant a resize drag finishes,
      // rather than leaving it visible until the pointer happens to move
      // off it - matches it only ever showing while genuinely in use.
      var wasResizing = !!(dragState && dragState.mode === 'resize');
      if (dragState && dragState.kind === 'photoOnly') canvas.style.cursor = 'grab';
      dragState = null;
      if (wasResizing) redraw();
    }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);
    canvas.addEventListener('pointerleave', function () {
      if (!dragState) canvas.style.cursor = '';
      if (handleHovered) {
        handleHovered = false;
        redraw();
      }
    });

    function getCurrentDesign() {
      var checked = designRadios.filter(function (radio) {
        return radio.checked;
      })[0];
      return checked ? designs[checked.value] : null;
    }

    function photoOnlyActive() {
      var cfg = getCurrentDesign();
      return !!(photoOnlyCheckbox && photoOnlyCheckbox.checked && !photoOnlyCheckbox.disabled && cfg && cfg.isPhotoUpload);
    }

    // While "Photo only" is ticked, the Name and Message fields are hidden
    // (so the validation in personalisation.js skips them - it ignores
    // anything inside a hidden container) and disabled (so nothing stale
    // is submitted with the order). Their values are kept, so unticking
    // brings the wording straight back.
    function applyPhotoOnlyFields(on) {
      [nameInput, line1Input].forEach(function (input) {
        if (!input) return;
        var wrap = input.closest('.personalisation-fields__field');
        if (wrap) wrap.hidden = on;
        input.disabled = on || (input === line1Input && isBrandedActive());
        if (on) {
          var errId = input.getAttribute('data-error-target');
          var errEl = errId ? document.getElementById(errId) : null;
          if (errEl) errEl.classList.add('hidden');
        }
      });
    }

    function getCurrentValues() {
      var branded = isBrandedActive();
      return {
        age: ageInput ? ageInput.value.trim() : '',
        name: nameInput ? nameInput.value.trim() : '',
        line1: branded
          ? brandedMessageInput ? brandedMessageInput.value.trim() : ''
          : line1Input ? line1Input.value.trim() : '',
        line2: branded ? '' : line2Input ? line2Input.value.trim() : '',
        companyName: companyNameInput ? companyNameInput.value.trim() : '',
        nameColour: branded ? groupColourHex('name') : null,
        messageColour: branded ? groupColourHex('message') : null,
        logo: currentLogoSource(),
        photo: rawPhotoImage,
        photoOnly: photoOnlyActive(),
        photoOffset: photoOnlyOffset
      };
    }

    var renderGeneration = 0;
    // Kept in sync by redraw() below - not directly awaited by the submit
    // listener any more (see that listener's comment), but harmless to keep
    // around in case future code wants to know when the canvas last settled.
    var canvasReadyPromise = Promise.resolve();

    function redraw() {
      // Cheap safety net - see syncOwnDesignExclusivity()'s own comment.
      syncOwnDesignExclusivity();
      syncTextColourForBackground();

      // Supersedes any redraw() still waiting on an image/font to finish
      // loading - once those finish, isStale() below tells them a newer
      // redraw has taken over and to skip painting.
      renderGeneration += 1;
      var thisGeneration = renderGeneration;
      function isStale() {
        return thisGeneration !== renderGeneration;
      }

      var cfg = getCurrentDesign();
      var isBrandedDesign = !!(cfg && cfg.isBranded);

      // Photo upload field visibility is per-DESIGN (see the data-photo-
      // upload-field comment above) - show/hide it here, and clear out any
      // previously-chosen photo the moment the customer switches to a
      // design that isn't a photo design, so it can never leak into a
      // different design's preview or get submitted against it.
      var isPhotoDesign = !!(cfg && cfg.isPhotoUpload);
      if (photoUploadField) photoUploadField.hidden = !isPhotoDesign;
      if (photoInput) photoInput.disabled = !isPhotoDesign;
      if (!isPhotoDesign && rawPhotoImage) {
        rawPhotoImage = null;
        photoTransform = null;
        if (photoInput) {
          photoInput.value = '';
          // V84: two "Choose file" buttons share this one input now (see the
          // data-photo-mode-trigger comment above) - reset both filename
          // indicators, not just one.
          Array.prototype.slice.call(block.querySelectorAll('[data-photo-filename]')).forEach(function (el) {
            el.textContent = 'No file chosen';
          });
        }
      }

      // A Photo Labels design only offers Name + one message line, not two
      // (Susie's request, 22 Sep) - leaves more room for the photo. Combines
      // with the branded/standard disable toggle in setFieldMode() below:
      // the field is only ever enabled while standard fields are showing AND
      // the current design isn't a photo design, so it can never be
      // submitted (or leak a stale value into a later design) in either
      // other case.
      // V82 "Photo only" - the tick box only applies to photo designs.
      if (photoOnlyCheckbox) {
        photoOnlyCheckbox.disabled = !isPhotoDesign;
        if (!isPhotoDesign && photoOnlyCheckbox.checked) {
          photoOnlyCheckbox.checked = false;
          photoOnlyOffset = 0;
        }
      }
      // V84: lightly highlight whichever of the two boxes matches
      // photoOnlyCheckbox's current state, so the customer can see which
      // mode is in effect - purely visual, see
      // .personalisation-fields__photo-mode-col--active in
      // snippets/personalisation-fields.liquid.
      photoModeTriggers.forEach(function (trigger) {
        var col = trigger.closest('.personalisation-fields__photo-mode-col');
        if (!col) return;
        var isActive = !!(isPhotoDesign && photoOnlyCheckbox &&
          (trigger.getAttribute('data-photo-mode-trigger') === 'photo-only') === photoOnlyCheckbox.checked);
        col.classList.toggle('personalisation-fields__photo-mode-col--active', isActive);
      });
      var photoOnlyOn = photoOnlyActive();
      if (isPhotoDesign || (nameInput && nameInput.disabled && !isBrandedActive())) {
        applyPhotoOnlyFields(photoOnlyOn);
      }
      if (photoQualityNote) {
        photoQualityNote.hidden = !(isPhotoDesign && rawPhotoImage && sourceHeight(rawPhotoImage) < 1000);
      }
      if (photoPanHint) {
        photoPanHint.hidden = !(photoOnlyOn && rawPhotoImage &&
          photoOnlyLayout(rawPhotoImage, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0).pannable);
      }

      if (line2Field) line2Field.hidden = isPhotoDesign;
      if (line2Input) {
        line2Input.disabled = isBrandedActive() || isPhotoDesign;
        if (isPhotoDesign && line2Input.value) line2Input.value = '';
      }

      var values = getCurrentValues();

      if (resizeHint) {
        resizeHint.hidden = !((isBrandedDesign && values.logo) || (isPhotoDesign && values.photo && !values.photoOnly));
      }

      // While "Upload your own ready-made design" has a file chosen, the
      // visible preview shows that file as-is (see drawOwnDesignPreview()
      // above) instead of the normal swatch-composited label - the customer
      // is printing that file exactly as supplied, so the ordinary Company
      // Name/logo compositing would be actively misleading here.
      var showOwnDesignPreview = ownDesignActive() && !!rawOwnDesignImage;

      // The resize handle only paints while the customer is actually
      // hovering it or mid-resize-drag - see the drawImageWithHandle() file
      // header comment. Irrelevant for the export canvas below (interactive:
      // false already skips the whole handle/outline block).
      var showHandle = handleHovered || !!(dragState && dragState.mode === 'resize');

      var pending = [
        showOwnDesignPreview
          ? drawOwnDesignPreview(canvas, rawOwnDesignImage)
          : drawLabel(canvas, cfg, values, {
              interactive: true,
              getLogoRect: isBrandedDesign ? getOrSeedLogoRect : null,
              getPhotoRect: isPhotoDesign ? getOrSeedPhotoRect : null,
              isStale: isStale,
              showHandle: showHandle
            })
      ];
      if (exportCanvas) {
        pending.push(
          drawLabel(exportCanvas, cfg, values, {
            interactive: false,
            getLogoRect: isBrandedDesign ? getOrSeedLogoRect : null,
            getPhotoRect: isPhotoDesign ? getOrSeedPhotoRect : null,
            isStale: isStale
          })
        );
      }
      canvasReadyPromise = Promise.all(pending).then(function () {
        // Keep the hidden Label Preview file input continuously in sync with
        // whatever's now on screen, rather than only exporting it at submit
        // time - see exportToFileInput()'s comment above and the submit
        // listener below. Skipped for a design-less/own-design state (no
        // fileInput target relevant, or cfg missing) and for a stale/
        // superseded redraw, same as the painting above.
        if (isStale()) return;
        if (fileInput && cfg && !ownDesignActive()) {
          if (labelRefInput && !labelRefInput.value) {
            labelRefInput.value = String(Math.floor(1000 + Math.random() * 9000));
          }
          exportToFileInput(fileInput, exportCanvas || canvas);
        }
      });
      return canvasReadyPromise;
    }

    var redrawTimer = null;
    function scheduleRedraw() {
      clearTimeout(redrawTimer);
      redrawTimer = setTimeout(redraw, 150);
    }

    // V81: the visible preview now renders at its on-screen size (see
    // canvasScaleFor()), so redraw it whenever that size changes - e.g. the
    // block first becomes visible, the window is resized or a phone is
    // rotated.
    if (typeof ResizeObserver !== 'undefined') {
      var lastPreviewWidth = 0;
      new ResizeObserver(function () {
        var w = canvas.clientWidth;
        if (w && Math.abs(w - lastPreviewWidth) > 1) {
          lastPreviewWidth = w;
          scheduleRedraw();
        }
      }).observe(canvas);
    }

    // V81: "View larger preview" - opens the label big in a pop-up so the
    // customer can see their wording clearly, especially on a phone. Drawn
    // from the full-size export canvas (or the customer's own uploaded
    // design, when that's what's being printed), shrunk smoothly.
    var enlargeButton = block.querySelector('[data-personalisation-enlarge]');
    if (enlargeButton) {
      enlargeButton.addEventListener('click', function () {
        Promise.resolve(canvasReadyPromise).then(function () {
          var overlay = document.createElement('div');
          overlay.className = 'personalisation-fields__lightbox';
          overlay.setAttribute('role', 'dialog');
          overlay.setAttribute('aria-modal', 'true');
          overlay.setAttribute('aria-label', 'Larger preview of your label');
          var inner = document.createElement('div');
          inner.className = 'personalisation-fields__lightbox-inner';
          var big = document.createElement('canvas');
          big.className = 'personalisation-fields__lightbox-canvas';
          var close = document.createElement('button');
          close.type = 'button';
          close.className = 'personalisation-fields__lightbox-close';
          close.setAttribute('aria-label', 'Close larger preview');
          close.textContent = '×';
          inner.appendChild(close);
          inner.appendChild(big);
          overlay.appendChild(inner);
          document.body.appendChild(overlay);
          var cssW = big.clientWidth || 900;
          var dpr = window.devicePixelRatio || 1;
          big.width = Math.round(cssW * dpr);
          big.height = Math.round(cssW * dpr * LOGICAL_HEIGHT / LOGICAL_WIDTH);
          var useOwn = ownDesignActive() && !!rawOwnDesignImage;
          if (useOwn) {
            drawOwnDesignPreview(big, rawOwnDesignImage);
          } else {
            stepDownDraw(exportCanvas || canvas, big);
          }
          function shut() {
            document.removeEventListener('keydown', onKey);
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
            enlargeButton.focus();
          }
          function onKey(e) {
            if (e.key === 'Escape') shut();
          }
          overlay.addEventListener('click', function (e) {
            if (e.target === overlay) shut();
          });
          close.addEventListener('click', shut);
          document.addEventListener('keydown', onKey);
          close.focus();
        });
      });
    }

    [nameInput, ageInput, line1Input, line2Input, companyNameInput, brandedMessageInput].forEach(function (input) {
      if (input) input.addEventListener('input', scheduleRedraw);
    });
    designRadios.forEach(function (radio) {
      radio.addEventListener('change', redraw);
    });

    if (logoInput) {
      logoInput.addEventListener('change', function () {
        var file = logoInput.files && logoInput.files[0];
        // The "Remove background automatically" checkbox itself is now
        // always visible (see snippets/personalisation-fields.liquid) so it
        // reliably reserves the height needed to keep "Upload your logo"
        // level with "Upload your own ready-made design" regardless of
        // whether a logo has been chosen yet - it simply has no visible
        // effect until there's an actual logo for it to apply to.
        if (!file) {
          rawLogoImage = null;
          processedLogoCanvas = null;
          logoTransform = null;
          redraw();
          return;
        }
        loadFileAsImage(file, function (img) {
          rawLogoImage = img;
          processedLogoCanvas = img ? stripNearWhiteBackground(img) : null;
          // A new logo file means any previous drag/resize no longer
          // applies - start fresh with the default centred/contained size.
          logoTransform = null;
          redraw();
        });
      });
    }

    if (removeBgCheckbox) {
      removeBgCheckbox.addEventListener('change', redraw);
    }

    if (ownDesignInput) {
      ownDesignInput.addEventListener('change', function () {
        // Keeps the canvas-export file input disabled/enabled in step with
        // whether a ready-made design is actually chosen here - see
        // syncOwnDesignExclusivity() above - then redraw() picks up the new
        // ownDesignActive() state itself (it already skips canvas export
        // while this is active, and the design-picker's own requirement is
        // skipped the same way - see validateGroup() in
        // assets/personalisation.js).
        syncOwnDesignExclusivity();
        var file = ownDesignInput.files && ownDesignInput.files[0];
        if (!file) {
          rawOwnDesignImage = null;
          redraw();
          return;
        }
        // Decoded purely so the live preview can show it as-is (see
        // drawOwnDesignPreview() above) - the actual file, untouched, is
        // still what's submitted on Add to cart, not this decoded copy.
        loadFileAsImage(file, function (img) {
          rawOwnDesignImage = img;
          redraw();
        });
      });
    }

    if (photoOnlyCheckbox) {
      photoOnlyCheckbox.addEventListener('change', function () {
        photoOnlyOffset = 0;
        redraw();
      });
    }

    // V84: clicking either "Choose file" button sets the hidden checkbox to
    // match BEFORE the file picker opens (this listener runs synchronously;
    // the browser's own "open the file picker" action for the label's "for"
    // happens straight after, as part of the same click) - so uploading
    // through a box both picks the file and sets the mode in one step. Sets
    // the hidden checkbox's own 'change' event, so the listener above (and
    // everything it triggers via redraw()) runs exactly as if the customer
    // had clicked the original tick box themselves.
    photoModeTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        if (!photoOnlyCheckbox) return;
        var wantPhotoOnly = trigger.getAttribute('data-photo-mode-trigger') === 'photo-only';
        if (photoOnlyCheckbox.checked !== wantPhotoOnly) {
          photoOnlyCheckbox.checked = wantPhotoOnly;
          photoOnlyCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // V84 follow-up (Susie, 5 Oct 2026): a "?" icon/popup explaining each
    // mode was tried here and removed the same day - Susie asked instead
    // for the short caption lines and fuller explanation to simply sit
    // permanently on the page (see the liquid comment above
    // data-photo-mode-group), so there is no click-to-reveal behaviour left
    // to wire up here.

    if (photoInput) {
      photoInput.addEventListener('change', function () {
        var file = photoInput.files && photoInput.files[0];
        // V84: both "Choose file" buttons point at this one shared input
        // (see the data-photo-mode-trigger comment above) - keep both
        // visible filename indicators in sync with whichever one was
        // actually used, rather than relying on the generic single-wrapper
        // [data-file-input] wiring further up, which only ever updates one.
        Array.prototype.slice.call(block.querySelectorAll('[data-photo-filename]')).forEach(function (el) {
          el.textContent = file ? file.name : 'No file chosen';
        });
        if (!file) {
          rawPhotoImage = null;
          photoTransform = null;
          redraw();
          return;
        }
        loadFileAsImage(file, function (img) {
          rawPhotoImage = img;
          photoOnlyOffset = 0;
          // A new photo file means any previous drag/resize no longer
          // applies - start fresh with the default centred/contained size,
          // same as a freshly-uploaded logo.
          photoTransform = null;
          redraw();
        });
      });
    }

    // Switching category (Branded Logo <-> any standard design category, in
    // either direction) clears whichever field set just became hidden, so a
    // half-filled form for one path never leaks into the other's preview or
    // cart properties. These listeners are independent of the category
    // picker's own show/hide script in personalisation-fields.liquid - both
    // simply react to the same clicks.
    function resetStandardFields() {
      if (nameInput) nameInput.value = '';
      if (ageInput) ageInput.value = '';
      if (line1Input) line1Input.value = '';
      if (line2Input) line2Input.value = '';
    }

    function resetBrandedFields() {
      if (companyNameInput) companyNameInput.value = '';
      if (brandedMessageInput) brandedMessageInput.value = '';
      if (logoInput) {
        logoInput.value = '';
        var wrapper = logoInput.closest('.personalisation-fields__file-field');
        var nameEl = wrapper ? wrapper.querySelector('[data-file-name]') : null;
        if (nameEl) nameEl.textContent = 'No file chosen';
      }
      if (removeBgCheckbox) removeBgCheckbox.checked = true;
      textColourGroups.forEach(function (group) {
        group.radios.forEach(function (radio) {
          radio.checked = false;
        });
        group.touched = false;
        group.last = null;
      });
      hideTextColourNotice();
      if (logoOnlyCheckbox) logoOnlyCheckbox.checked = false;
      logoOnlyHideEls.forEach(function (el) {
        el.hidden = false;
      });
      if (companyNameInput) {
        companyNameInput.setAttribute('data-required-path', 'standard');
        companyNameInput.disabled = false;
      }
      rawLogoImage = null;
      processedLogoCanvas = null;
      logoTransform = null;
    }

    function setFieldMode(branded) {
      if (standardFields) standardFields.hidden = !!branded;
      if (brandedFields) brandedFields.hidden = !branded;
      // See the disabled-on-init comment above - keep the inactive Message
      // input disabled so only the active one is ever included in the
      // cart's FormData.
      if (line1Input) line1Input.disabled = !!branded;
      if (line2Input) line2Input.disabled = !!branded;
      if (brandedMessageInput) brandedMessageInput.disabled = !branded;
      if (branded) {
        resetStandardFields();
      } else {
        // Leaving Corporate Branding entirely for a different category - a
        // stale ready-made-design upload shouldn't carry over into a fresh
        // pass through a standard category, so clear it here too, alongside
        // the rest of the branded fields.
        resetBrandedFields();
        resetOwnDesignField();
      }
      syncOwnDesignExclusivity();
      redraw();
    }

    if (brandedFields) {
      Array.prototype.slice.call(block.querySelectorAll('[data-category-select]')).forEach(function (tile) {
        tile.addEventListener('click', function () {
          setFieldMode(tile.getAttribute('data-category-select') === 'branded-logo');
        });
      });
      Array.prototype.slice.call(block.querySelectorAll('[data-category-back]')).forEach(function (backLink) {
        backLink.addEventListener('click', function () {
          setFieldMode(false);
        });
      });
    }

    redraw();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(redraw).catch(function () {});
    }

    // block sits as a sibling of the buy-buttons block's <form>, not inside
    // it (see snippets/personalisation-fields.liquid), so its own form id is
    // exposed via data-personalisation-form-id - block.closest('form') would
    // always be null here.
    var formId = block.getAttribute('data-personalisation-form-id');
    var form = (formId && document.getElementById(formId)) || block.closest('form');
    if (form && fileInput && typeof DataTransfer !== 'undefined') {
      // Historical note (removed 22 Sep): this used to preventDefault the
      // submit, force one more async redraw(), wait for it, export the
      // canvas, THEN re-dispatch a synthetic submit via form.requestSubmit()
      // - flagged via a resubmitInFlight boolean so the second, synthetic
      // submit could sail through this same listener without looping. That
      // turned out to be fragile in a way that produced exactly the
      // "clicking Add to Cart does nothing, second click works" bug Susie
      // reported on 22 Sep: whatever the precise cause (the resubmit not
      // completing cleanly, the flag being left in the wrong state, or
      // similar), the net effect was the first, genuine click going nowhere
      // and only a second, unrelated click's submit slipping through
      // un-intercepted.
      //
      // Replaced with a much simpler, non-blocking approach: the hidden
      // Label Preview file input is now kept continuously up to date by
      // redraw() itself (see exportToFileInput() above and its call inside
      // redraw()), which already reruns on every field change, design pick
      // and logo/photo upload - so by the time the customer actually clicks
      // Add to Cart, the exported image is already attached and there is
      // nothing left to wait for. This listener now only stamps a fresh
      // Label Reference, synchronously, and never prevents or delays the
      // submit - Add to Cart always goes through on the very first click.
      document.addEventListener(
        'submit',
        function (event) {
          if (event.target !== form) return;
          if (labelRefInput) {
            labelRefInput.value = String(Math.floor(1000 + Math.random() * 9000));
          }
          // Best-effort, synchronous re-export of whatever is currently
          // painted (covers the rare case of a keystroke still sitting in
          // scheduleRedraw()'s 150ms debounce at the moment of submit) -
          // uses the canvas as it stands right now, no waiting, no
          // preventing the submit.
          if (!ownDesignActive()) {
            var cfg = getCurrentDesign();
            if (cfg) exportToFileInput(fileInput, exportCanvas || canvas);
          }
        },
        true
      );
    }
  }

  function initAllBlocks() {
    var blocks = document.querySelectorAll('[data-personalisation-fields]');
    blocks.forEach(initBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllBlocks);
  } else {
    initAllBlocks();
  }
})();